import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { AdGroup } from '@/types/sem';
import type { SeverityLevel } from './story-utils';
import { fmtPct, fmtNum } from './story-utils';
import type { ReactNode } from 'react';

interface AdGroupSectionProps {
  adGroupName: string;
  adGroup: AdGroup | null;
  severity: SeverityLevel;
  pageCount: number;
  children: ReactNode;
  defaultOpen?: boolean;
}

const SEVERITY_LABEL: Record<SeverityLevel, { text: string; className: string }> = {
  critical: { text: 'Critical', className: 'bg-[var(--ed-red-bg)] text-[var(--ed-red)] border-[rgba(224,90,82,0.12)]' },
  warning: { text: 'Warning', className: 'bg-[var(--ed-amber-bg)] text-[var(--ed-amber)] border-[rgba(224,168,82,0.12)]' },
  ok: { text: 'Healthy', className: 'bg-[var(--ed-green-bg)] text-[var(--ed-green)] border-[rgba(74,186,122,0.12)]' },
};

export function AdGroupSection({ adGroupName, adGroup, severity, pageCount, children, defaultOpen = false }: AdGroupSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const sev = SEVERITY_LABEL[severity];
  const m = adGroup?.metrics;

  return (
    <div className={`ag-section ${open ? 'ag-section-open' : ''}`}>
      <button
        className="ag-header ag-header-toggle"
        onClick={() => setOpen(!open)}
      >
        <div className="ag-header-left">
          <ChevronDown
            size={16}
            className={`ag-chev ${open ? 'ag-chev-open' : ''}`}
          />
          <h2 className="ag-title">{adGroupName}</h2>
          <span className={`ag-badge border ${sev.className}`}>{sev.text}</span>
          <span className="ag-page-count">{pageCount} {pageCount === 1 ? 'page' : 'pages'}</span>
        </div>
        {m && (
          <div className="ag-metrics">
            <span>CTR {fmtPct(m.ctr)}</span>
            <span className="ag-metrics-sep">&middot;</span>
            <span>CPC ${m.cpc.toFixed(2)}</span>
            <span className="ag-metrics-sep">&middot;</span>
            <span>{fmtNum(m.clicks)} clicks</span>
            <span className="ag-metrics-sep">&middot;</span>
            <span>{fmtNum(m.impressions)} impr</span>
          </div>
        )}
      </button>
      {open && (
        <div className={`ag-body ag-body-${severity}`}>
          {children}
        </div>
      )}
    </div>
  );
}
